import React from "react";
import { Box } from "@mui/material";
import Section, { Inner } from "../ui/Section";
import Figure from "../ui/Figure";
import { ArrowLink } from "../ui/Button";
import { Accent, Display, Label } from "../ui/Type";
import { MEDIA } from "../../data/media";
import { getProjectById, FEATURED_PROJECT_ID } from "../../data/projects";
import { color, space, type } from "../../design/tokens";

/**
 * One project, given the whole width of the page.
 *
 * The lead photograph runs full-bleed — the only place on the site where an
 * image touches both edges — with the supporting shots as a strip beneath.
 * The full-bleed plate is the one deliberate exception to the portrait rule:
 * at 16:9 it reads as a hero, and the bathroom photographs survive that crop
 * because their subject sits in the middle band of the frame.
 */
const FeaturedProject = () => {
  const project = getProjectById(FEATURED_PROJECT_ID);
  if (!project) return null;

  const [lead, ...supporting] = project.images;

  return (
    <Section tone="ink" bleed>
      <Inner>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
            gap: { xs: 3, md: 8 },
            alignItems: "end",
            marginBottom: { xs: 5, md: 7 },
          }}
        >
          <Box>
            <Label tone="onInk">Featured project</Label>
            <Display as="h2" size="lg" tone="onInk" sx={{ marginTop: 3, maxWidth: 640 }}>
              {project.featuredHeadline || project.title},{" "}
              <Accent sx={{ color: color.clayLight }}>then rebuilt</Accent>.
            </Display>
          </Box>

          <Box
            component="dl"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(2, auto)" },
              gap: { xs: 2.5, md: 6 },
              justifyContent: { md: "end" },
              margin: 0,
              paddingBottom: 1,
            }}
          >
            {[
              { term: "Category", value: project.category },
              { term: "Trades", value: project.trades || "Self-performed" },
            ].map((item) => (
              <Box key={item.term}>
                <Box component="dt" sx={{ ...type.label, color: color.clayLight, marginBottom: 1 }}>
                  {item.term}
                </Box>
                <Box component="dd" sx={{ ...type.bodySm, color: color.onInk, margin: 0 }}>
                  {item.value}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Inner>

      {/* Full-bleed lead plate — the one place a photo runs edge to edge. It's
          framed 5:4 rather than a wide letterbox so the staircase survives the
          crop; below md it reverts to the source 4:5. */}
      <Box sx={{ marginBottom: { xs: 4, md: 6 } }}>
        <Figure
          media={lead}
          ratio={{ xs: "4 / 5", md: "16 / 9" }}
          tone="onInk"
          caption={false}
          objectPosition="center 40%"
        />
      </Box>

      <Inner>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
            gap: { xs: 5, md: 10 },
            alignItems: "start",
          }}
        >
          <Box>
            <Box
              component="p"
              sx={{ ...type.lead, color: color.onInk, margin: 0, maxWidth: space.measure }}
            >
              {project.summary}
            </Box>
            <Box
              component="p"
              sx={{ ...type.body, color: color.onInkMute, marginTop: 3 }}
            >
              {project.description}
            </Box>
            <Box sx={{ marginTop: 4 }}>
              <ArrowLink to="/projects" tone="onInk">
                View all projects
              </ArrowLink>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(supporting.length, 3)}, 1fr)`,
              gap: { xs: 2, md: 3 },
            }}
          >
            {supporting.slice(0, 3).map((key) => (
              <Figure
                key={key}
                media={key}
                ratio="4 / 5"
                tone="onInk"
                captionText={MEDIA[key]?.caption}
              />
            ))}
          </Box>
        </Box>
      </Inner>
    </Section>
  );
};

export default FeaturedProject;
